import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material';
import { WalteroCard, WalteroTypography, WalteroChart } from '../waltero';
import { useSharedPerformanceCache } from '../../context/SharedPerformanceCache';

const BatteryAnalytics = () => {
  const theme = useTheme();
  const { getEndpointData, getDevices } = useSharedPerformanceCache();

  // Battery box plot data processing using SharedPerformanceCache
  const { batteryBoxPlotData, dates } = useMemo(() => {
    console.log('🔋 Processing battery data from cache...');
    
    const devices = getDevices();
    if (!devices || devices.length === 0) {
      return { batteryBoxPlotData: {}, dates: [] };
    }

    // Get all dates from battery data across all devices
    const allDates = new Set();
    devices.forEach(device => {
      const batteryData = getEndpointData('battery-daily', device.id);
      if (batteryData?.dailyBattery && Array.isArray(batteryData.dailyBattery)) {
        batteryData.dailyBattery.forEach(day => {
          if (day.date) allDates.add(day.date);
        });
      }
    });

    const dateArray = Array.from(allDates).sort().reverse(); // Newest first (to match table ordering)
    console.log('🔋 Battery dates found (newest first):', dateArray);

    // Process battery data for each date - use device averages for that day
    const batteryPlotData = {};
    dateArray.forEach(date => {
      const dayVoltages = []; // Collect all device averages for this day
      
      devices.forEach(device => {
        const batteryData = getEndpointData('battery-daily', device.id);
        const dayData = batteryData?.dailyBattery?.find(day => day.date === date);
        
        // Use each device's daily average voltage for this date
        if (dayData?.averageVoltage && dayData.averageVoltage > 0) {
          dayVoltages.push(dayData.averageVoltage);
        }
      });

      if (dayVoltages.length > 0) {
        // Calculate box plot statistics from device averages
        const sorted = [...dayVoltages].sort((a, b) => a - b);
        const q1Index = Math.floor(sorted.length * 0.25);
        const q2Index = Math.floor(sorted.length * 0.5);
        const q3Index = Math.floor(sorted.length * 0.75);
        
        const q1 = sorted[q1Index];
        const q2 = sorted[q2Index]; // median
        const q3 = sorted[q3Index];
        const iqr = q3 - q1;
        
        // Calculate outliers
        const lowerBound = q1 - 1.5 * iqr;
        const upperBound = q3 + 1.5 * iqr;
        const outliers = dayVoltages.filter(v => v < lowerBound || v > upperBound);
        
        // Calculate whiskers
        const nonOutliers = dayVoltages.filter(v => v >= lowerBound && v <= upperBound);
        const lowerWhisker = nonOutliers.length > 0 ? Math.min(...nonOutliers) : q1;
        const upperWhisker = nonOutliers.length > 0 ? Math.max(...nonOutliers) : q3;
        
        const min = Math.min(...dayVoltages);
        const max = Math.max(...dayVoltages);

        batteryPlotData[date] = {
          q1, q2, q3, iqr,
          lowerWhisker, upperWhisker,
          outliers,
          min,
          max,
          count: dayVoltages.length
        };
      }
    });

    console.log('🔋 Processed battery box plot data:', batteryPlotData);
    console.log('🔋 Final date order for chart (newest first):', dateArray);
    console.log('🔋 Data alignment check:', dateArray.map(date => ({
      date,
      hasData: !!batteryPlotData[date],
      median: batteryPlotData[date]?.q2?.toFixed(3),
      deviceCount: batteryPlotData[date]?.count
    })));
    
    return {
      batteryBoxPlotData: batteryPlotData,
      dates: dateArray
    };
  }, [getDevices, getEndpointData]);

  return (
    <div className="battery-analytics-container">
      {/* Battery Voltage Distribution Box Plot */}
      {Object.keys(batteryBoxPlotData).length > 0 ? (
        <div style={{ marginBottom: '20px' }}>
          <WalteroTypography variant="h6" gutterBottom sx={{ color: '#2c3e50', fontWeight: '600' }}>
            Daily Battery Voltage Distribution
          </WalteroTypography>
          <WalteroCard sx={{ p: 3 }}>
            <WalteroChart
              option={{
                tooltip: {
                  trigger: 'axis',
                  formatter: function(params) {
                    const data = params[0];
                    const date = data.name;
                    const stats = batteryBoxPlotData[date];
                    return `${date}<br/>Min: ${(stats.min || 0).toFixed(3)}V<br/>Q1: ${(stats.q1 || 0).toFixed(3)}V<br/>Median: ${(stats.q2 || 0).toFixed(3)}V<br/>Q3: ${(stats.q3 || 0).toFixed(3)}V<br/>Max: ${(stats.max || 0).toFixed(3)}V<br/>Devices: ${stats.count || 0}`;
                  }
                },
                xAxis: {
                  type: 'category',
                  data: dates, // Use the sorted dateArray to ensure proper order
                  name: 'Date'
                },
                yAxis: {
                  type: 'value',
                  name: 'Battery Voltage (V)',
                  min: 'dataMin',
                  max: 'dataMax'
                },
                series: [{
                  name: 'Battery Voltage',
                  type: 'boxplot',
                  data: dates.map(date => {
                    const stats = batteryBoxPlotData[date];
                    return stats ? [
                      stats.min, stats.q1, stats.q2, stats.q3, stats.max
                    ] : null;
                  }).filter(Boolean) // Remove null entries
                }]
              }}
              height={400}
              loading={false}
              style={{ 
                width: '100%',
                minHeight: 400
              }}
              aria-label="Daily battery voltage distribution box plot showing quartiles across device fleet"
            />
          </WalteroCard>
        </div>
      ) : (
        <div>🔋 No battery data available for box plot</div>
      )}
    </div>
  );
};

BatteryAnalytics.propTypes = {};

export default BatteryAnalytics;
